import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { loadGalleryImages, GalleryImage } from '../utils/imageLoader';
import { ImagesHero } from '../components/images';
import { FloatingPattern } from '../components/common';

const PageContainer = styled.div`
    position: relative;
    padding-top: 70px;
    overflow-x: hidden;
`;

const GallerySection = styled.section`
    width: 100%;
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => theme.spacing.xxl} 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: ${({ theme }) => theme.spacing.xl} 0;
    }
`;

const GalleryGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.lg};
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.xxl};
    min-height: 800px; /* Fixed height for 6 images in 2 rows */

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        gap: ${({ theme }) => theme.spacing.md};
        padding: 0 ${({ theme }) => theme.spacing.md};
        min-height: 600px;
    }
`;

const ImageCard = styled.div<{ $delay: number; $isPlaceholder?: boolean }>`
    position: relative;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    background-color: ${({ theme, $isPlaceholder }) =>
        $isPlaceholder ? theme.colors.backgroundLighter : theme.colors.backgroundLighter};
    cursor: ${({ $isPlaceholder }) => ($isPlaceholder ? 'default' : 'pointer')};
    width: calc(25% - ${({ theme }) => theme.spacing.lg});
    aspect-ratio: 1;
    opacity: ${({ $isPlaceholder }) => ($isPlaceholder ? 1 : 0)};
    transform: translateY(20px);
    animation: ${({ $isPlaceholder }) => ($isPlaceholder ? 'none' : 'fadeInUp 1.9s ease forwards')};
    animation-delay: ${({ $delay }) => $delay}s;
    transition: transform 0.3s ease;

    @keyframes fadeInUp {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    &:hover {
        transform: ${({ $isPlaceholder }) => ($isPlaceholder ? 'none' : 'scale(1.05)')};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
        width: calc(33.333% - ${({ theme }) => theme.spacing.lg});
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: calc(50% - ${({ theme }) => theme.spacing.md});
    }
`;

const GalleryImageEl = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const LoadingMessage = styled.p`
    text-align: center;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    padding: ${({ theme }) => theme.spacing.xl};
`;

const Lightbox = styled.div<{ $show: boolean }>`
    display: ${({ $show }) => ($show ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.95);
    z-index: 1000;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.xl};
`;

const LightboxImage = styled.img`
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
`;

const CloseButton = styled.button`
    position: absolute;
    top: ${({ theme }) => theme.spacing.lg};
    right: ${({ theme }) => theme.spacing.lg};
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.text};
    font-size: 2rem;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing.sm};

    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

// Update this URL to your CloudFront distribution
const GALLERY_BASE_URL = 'https://d110v70ry4kxfb.cloudfront.net/images/gallery/';

export const ImagesPage: React.FC = () => {
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        const loadImages = async () => {
            setLoading(true);
            // Load exactly 6 images (01.jpg through 06.jpg)
            const loadedImages = await loadGalleryImages(GALLERY_BASE_URL, 'jpg', 6);
            setImages(loadedImages);
            setLoading(false);
        };

        loadImages();
    }, []);

    const handleImageClick = (url: string) => {
        setSelectedImage(url);
    };

    const handleCloseLightbox = () => {
        setSelectedImage(null);
    };

    return (
        <PageContainer>
            <FloatingPattern />
            <ImagesHero />

            <GallerySection>
                <GalleryGrid>
                    {loading ? (
                        // Show 6 placeholder cards while loading
                        Array.from({ length: 6 }).map((_, idx) => (
                            <ImageCard
                                key={`placeholder-${idx}`}
                                $delay={0}
                                $isPlaceholder={true}
                            />
                        ))
                    ) : images.length === 0 ? (
                        <LoadingMessage>No images found in gallery.</LoadingMessage>
                    ) : (
                        images.map((image, idx) => (
                            <ImageCard
                                key={image.index}
                                $delay={idx * 0.35}
                                onClick={() => handleImageClick(image.url)}
                            >
                                <GalleryImageEl
                                    src={image.url}
                                    alt={`Gallery image ${image.index}`}
                                />
                            </ImageCard>
                        ))
                    )}
                </GalleryGrid>
            </GallerySection>

            <Lightbox $show={selectedImage !== null} onClick={handleCloseLightbox}>
                <CloseButton onClick={handleCloseLightbox}>×</CloseButton>
                {selectedImage && <LightboxImage src={selectedImage} alt="Full size" />}
            </Lightbox>
        </PageContainer>
    );
};
